import { GetTokenSilentlyOptions } from '../lib/auth'
import { jwtDecode } from 'jwt-decode'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { accessTokenProvider } from '../lib/access-token.provider'

interface UserMetadata {
  tenantId: number
  tenantName: string
  userId: string
  permissions: string[]
  isSuperUser: boolean
}
interface CustomClaims {
  sub: string
}

export class AuthStore {
  token = ''
  isLoaded = false
  claims = {} as CustomClaims
  metadata = {} as UserMetadata

  constructor() {
    makeObservable(this, {
      token: observable,
      isLoaded: observable,
      claims: observable,
      metadata: observable,

      fetchAuth: action,
      setMetadata: action,
      setAsLoaded: action,

      permissions: computed,
      userId: computed,
      tenantId: computed,
      isSuperUser: computed,
    })
  }

  async fetchAuth(getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>) {
    accessTokenProvider.setAccessTokenFactory(getAccessTokenSilently)
    const accessToken = await getAccessTokenSilently()

    runInAction(() => {
      this.claims = jwtDecode(accessToken)
      this.token = accessToken
    })
  }

  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission)
  }

  isAdmin(): boolean {
    return this.hasPermission('admin')
  }

  setMetadata(metadata: UserMetadata) {
    this.metadata = metadata
  }

  setAsLoaded() {
    this.isLoaded = true
  }

  get permissions() {
    return this.metadata.permissions ?? []
  }

  get userId() {
    return this.metadata.userId ?? this.claims.sub
  }

  get tenantId() {
    return this.metadata.tenantId
  }

  get tenantName() {
    return this.metadata.tenantName
  }

  get isSuperUser() {
    return this.metadata.isSuperUser
  }
}

export const authStore = new AuthStore()
